import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "ilmli.cookie_ack";

/**
 * One-cookie banner. We don't run analytics or ads, so this is a
 * lightweight "you understand we set a session cookie" notice rather
 * than a full Granular Consent Manager. The user must explicitly
 * acknowledge before it disappears; the ack persists in localStorage so
 * we don't nag.
 *
 * Hidden in two cases:
 *   - already acknowledged (localStorage flag)
 *   - on /privacy or /terms (the user is already reading the policy)
 */
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const ack = localStorage.getItem(STORAGE_KEY);
      const path = window.location.pathname;
      const onPolicy = path === "/privacy" || path === "/terms";
      setOpen(!ack && !onPolicy);
    } catch {
      setOpen(false);
    }
  }, []);

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50 rounded-md bg-ink-card text-paper shadow-float p-4 md:p-5"
    >
      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-paper/55 mb-2">
        cookie notice
      </div>
      <p className="font-serif text-[14px] leading-snug text-paper">
        ilmli sets <strong className="not-italic font-medium">one cookie</strong> after you sign in
        — a session token so you stay logged in. No analytics, no ads, no third-party trackers.
        Read the full{" "}
        <Link to="/privacy" className="underline decoration-paper/40 hover:decoration-terracotta">
          privacy policy
        </Link>
        .
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-md bg-terracotta text-paper px-3.5 py-1.5 text-[13px] font-medium hover:bg-terracotta-soft"
        >
          OK
        </button>
        <Link
          to="/privacy"
          onClick={dismiss}
          className="font-mono text-[11px] text-paper/70 underline decoration-paper/30 hover:decoration-terracotta"
        >
          Read more
        </Link>
      </div>
    </div>
  );
}
